import type { Map as ImmutableMap } from 'immutable';
import { call, delay, put, select, takeEvery, takeLeading } from 'redux-saga/effects';

import { accountsActions, accountsTypes } from 'app-components/flux/accounts';
import { getEnvironmentConfig, getLogger, httpClientInterface } from 'app-components/utils/core';

import * as actions from './actions';
import { getFundingAccountsById } from './selectors';
// import { getAccountByFundingAccountId } from './selectors';
import type { FundingAccount, FundingAccountProps } from './types';
import { EligigibilityGroupEnum, mkFundingAccount, verificationStatusEnum } from './types';

const log = getLogger('data/fundingAccount/fundingAccountSagas.ts');

type genericResponse<T> = {
  config: any;
  data: T;
  headers: any;
  request: any;
  status: number;
  statusText: string;
};

type FundingAccountsReturn = {
  eligible: FundingAccountProps[];
  pendingVerification: FundingAccountProps[];
  available: FundingAccountProps[];
  inactive: FundingAccountProps[];
};

export function* getFundingAccountsActionWatcher() {
  yield takeLeading(actions.getFundingAccounts.type, function* handle() {
    try {
      const requestUrl = `${
        getEnvironmentConfig().services_url
      }/aikya-funding-account/api/v1/funding-accounts?intent=funds-transfer`;
      const response: genericResponse<FundingAccountsReturn> = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
      );
      log.log('getFundingAccounts, response=', response);
      const resources: FundingAccount[] = [];
      // this is gross, but QCS is returning resources in specfic buckets that we map to our flat 'resources' list
      response?.data?.eligible?.forEach((fundingAccount) => {
        fundingAccount?.id &&
          resources.push(
            mkFundingAccount({
              ...fundingAccount,
              eligibility: EligigibilityGroupEnum.eligible,
            }),
          );
      });
      response?.data?.pendingVerification?.forEach((fundingAccount) => {
        fundingAccount?.id &&
          resources.push(
            mkFundingAccount({
              ...fundingAccount,
              eligibility: EligigibilityGroupEnum.pendingVerification,
            }),
          );
      });
      response?.data?.available?.forEach((fundingAccount) => {
        fundingAccount?.id &&
          resources.push(
            mkFundingAccount({
              ...fundingAccount,
              eligibility: EligigibilityGroupEnum.available,
            }),
          );
      });
      response?.data?.inactive?.forEach((fundingAccount) => {
        fundingAccount?.id &&
          resources.push(
            mkFundingAccount({
              ...fundingAccount,
              eligibility: EligigibilityGroupEnum.inactive,
            }),
          );
      });
      log.log('FundingAccount compiled resources:', resources);
      yield put(actions.getFundingAccountsSuccess(resources)); // sending reducer array of Records
    } catch (e: any) {
      log.warn('Error in fundingAccounts GET\n', e);
      yield put(actions.getFundingAccountsFailure(e));
    }
  });
}

function* doCreate(
  payload: FundingAccount | FundingAccountProps,
  reject?: ((error: any) => void) | undefined,
): Generator<any, FundingAccount | null, any> {
  try {
    const requestUrl = `${
      getEnvironmentConfig().services_url
    }/aikya-funding-account/api/v1/funding-account?intent=funds-transfer`;
    const response: genericResponse<FundingAccountProps> = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      'toJS' in payload ? payload.toJS() : payload, // transpose to JS obj if payload is record
    );
    log.log('FundingAccount create, response=', response);
    const newFundingAccount = mkFundingAccount(response.data); // mk record before sending to reducer

    yield put(actions.createFundingAccountSuccess(newFundingAccount));
    // add FA id to account
    yield put(
      accountsActions.updateAccount(
        accountsTypes.mkUpdateAccount(newFundingAccount.destinationOnly ? 'INVESTMENT' : 'BANK', {
          id: newFundingAccount.linkedAccountId,
          fundingAccountId: newFundingAccount.id,
        }),
      ),
    );
    return newFundingAccount;
  } catch (e: any) {
    log.warn('Error in fundingAccount create request\n', e);
    reject?.(e);
  }
  return null;
}

const lastFour = (numStr?: string) => (numStr ? numStr.slice?.(-4) : '');

export function* createFundingAccountActionWatcher() {
  yield takeEvery(
    actions.createFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.createFundingAccount>) {
      try {
        const { payload } = action;
        const fundingAccounts: ImmutableMap<string, FundingAccount> = yield select(getFundingAccountsById);

        // TODO: Matching accounts currently does not work properly because we are not altering the funding account values appropriately and might even
        // TODO: connect to an older incorrect account that was made manual. We need to account for this scenario
        // TODO: Do we also reset the amount of microdeposit attempts if reconnecting? OR undo fail states?
        // check to make sure we don't have an orphaned funding account already present
        const matchingAccount = fundingAccounts.find(
          (acc) =>
            lastFour(acc?.accountNumber) === lastFour(payload?.accountNumber) &&
            lastFour(acc?.routingNumber) === lastFour(payload?.routingNumber) &&
            acc.eligibility !== EligigibilityGroupEnum.inactive,
        );

        let fundingAccountToConfirm: FundingAccount | null = null;

        if (matchingAccount?.id) {
          log.log('matching funding account found:\n', matchingAccount, '\nLinking to existing funding account');

          if (matchingAccount.linkedAccountId === payload.linkedAccountId) {
            // add orphaned FA to account
            yield put(
              accountsActions.updateAccount(
                accountsTypes.mkUpdateAccount(payload.destinationOnly ? 'INVESTMENT' : 'BANK', {
                  id: payload.linkedAccountId,
                  fundingAccountId: matchingAccount.id,
                }),
              ),
            );
            fundingAccountToConfirm = matchingAccount;
          } else {
            // funding account has outdated linkedAccountId
            yield put(actions.deleteFundingAccount({ id: matchingAccount.id, isDeleted: true }));
            yield delay(3000); // some time for funding account to be cleared on QCS
            fundingAccountToConfirm = yield doCreate(payload, action.meta?.reject);
          }
        } else {
          fundingAccountToConfirm = yield doCreate(payload, action.meta?.reject);
        }

        log.log('FundingAccount to confirm:', fundingAccountToConfirm);

        if (
          fundingAccountToConfirm &&
          fundingAccountToConfirm.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_REQUIRED
        ) {
          log.log('Beginning Funding confirmation');
          yield put(actions.confirmFundingAccount(fundingAccountToConfirm, action.meta));
        } else {
          action.meta?.resolve?.(fundingAccountToConfirm);
        }
      } catch (e: any) {
        log.warn('Error in fundingAccount createHandler\n', e);
        yield put(actions.createFundingAccountFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export function* confirmFundingAccountActionWatcher() {
  yield takeEvery(
    actions.confirmFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.confirmFundingAccount>) {
      try {
        const { payload } = action;
        const requestUrl = `${getEnvironmentConfig().services_url}/aikya-funding-account/api/v1/funding-account/${
          payload.id
        }{/confirm?intent=funds-transfer`;
        const response: genericResponse<any> = yield call(
          httpClientInterface.send,
          'PUT',
          requestUrl,
          null, // info appeneded to the requestURL
        );
        log.log('FundingAccount confirm, response=', response);
        yield delay(100); // QCS time to update DB
        yield put(actions.getFundingAccounts()); // GET because that has proper verification status enum
        yield delay(2000); // some time for funding account to be retrieved QCS before updating back to the UI
        action.meta?.resolve?.(payload);
      } catch (e: any) {
        log.warn('Error in fundingAccount confirm request\n', e);
        yield put(actions.confirmFundingAccountFailure(e));
        // GET to ensure we have the most updated eligibility + verification status
        yield put(actions.getFundingAccounts()); // GET to ensure we have the most updated eligibility + verification status
        action.meta?.reject?.(e);
      }
    },
  );
}

type VerifyReturn = {
  verificationStatus: string; // this is not the same VerficationStatus returned in the GET
  attempts: number;
  threshold: number;
};
export function* verifyMicroDespositsActionWatcher() {
  yield takeEvery(
    actions.verifyMicroDepositsAction.type,
    function* handle(action: ReturnType<typeof actions.verifyMicroDepositsAction>) {
      try {
        const { id, amountOne, amountTwo } = action.payload;
        const requestUrl = `${
          getEnvironmentConfig().services_url
        }/aikya-funding-account/api/v1/account-verification/${id}/verify-micro-deposits`;
        const response: genericResponse<VerifyReturn> = yield call(httpClientInterface.send, 'PUT', requestUrl, {
          amount_one: amountOne,
          amount_two: amountTwo,
        });
        log.log('Verify MicroDeposits, response=', response);
        // TODO: maybe check response code before doing a fresh GET?
        yield put(actions.getFundingAccounts()); // GET because that has proper verification status enum
        action.meta?.resolve?.(response);
      } catch (e: any) {
        log.warn('Error in verify micoDeposits request\n', e);
        yield put(actions.verifyMicroDepositsActionFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export function* deleteFundingAccountActionWatcher() {
  yield takeEvery(
    actions.deleteFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.deleteFundingAccount>) {
      try {
        const { id } = action.payload;
        const requestUrl = `${getEnvironmentConfig().services_url}/aikya-funding-account/api/v1/funding-account/${id}`; // maybe add other params?
        const response: genericResponse<any> = yield call(httpClientInterface.send, 'DELETE', requestUrl, undefined);
        log.log('Delete FundingAccount, response=', response);
        // QCS isn't returning the deleted resource, so we have to prune ourselves
        const fundingAccounts: ImmutableMap<string, FundingAccount> = yield select(getFundingAccountsById);
        const deletedFundingAccount = fundingAccounts?.get(id)?.set('isDeleted', true);
        if (deletedFundingAccount) {
          yield put(actions.deleteFundingAccountSuccess(deletedFundingAccount));
        }
        action.meta?.resolve?.(response);
      } catch (e: any) {
        log.warn('Error in deleteFundingAccount request\n', e);
        yield put(actions.deleteFundingAccountFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

// export default [
//   getFundingAccountsActionWatcher,
//   createFundingAccountActionWatcher,
//   createFundingAccountSuccessActionWatcher,
//   updateFundingAccountActionWatcher,
//   deleteFundingAccountActionWatcher,
//   deleteFundingAccountSuccessActionWatcher,
//   batchCreateFundingAccountsActionWatcher,

//   initiateMicroDepositActionWatcher,
//   verifyMicroDepositActionWatcher,
// ];

export default [
  getFundingAccountsActionWatcher,
  createFundingAccountActionWatcher,
  confirmFundingAccountActionWatcher,
  verifyMicroDespositsActionWatcher,
  deleteFundingAccountActionWatcher,
];
